import { Link, useNavigate } from 'react-router';

import { Button, Card, cn } from '@/design-system';
import type { OrderProblem } from '@/entities/order';

import { paths } from '@/app/router/paths';
import { inspectionsCopy } from '../copy';
import { problemText } from '../problemText';

/**
 * Een mislukte schrijfactie in gewone taal. Bij een factuur die al bestaat hoort
 * er een weg naar Facturatie bij, niet alleen een melding.
 *
 * Zijn de keuringen op, dan is dat geen fout van de gebruiker maar een moment om
 * er meer te nemen: een rustig blok met de uitleg en de twee wegen verder, geen
 * rood. Met een abonnement zijn dat extra keuringen kopen (nu) en een groter
 * abonnement (vanaf de volgende maand); zonder abonnement, ook in de proef, een
 * abonnement of een losse rapportage. Beide knoppen gaan naar hun blok op
 * Facturatie.
 */
export function ProblemLine({ problem, className }: { problem: OrderProblem; className?: string }) {
    const navigate = useNavigate();

    if (problem.kind === 'credits') {
        const ways = problem.subscription
            ? [
                  { label: inspectionsCopy.buyExtraInspections, to: paths.buyInspections },
                  { label: inspectionsCopy.chooseBiggerPlan, to: paths.choosePlan },
              ]
            : [
                  { label: inspectionsCopy.choosePlan, to: paths.choosePlan },
                  { label: inspectionsCopy.buySingleReport, to: paths.buyInspections },
              ];

        return (
            <Card padding="md" role="status" className={className}>
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                    {problemText(problem)}
                </p>
                <div className="mt-[10px] flex flex-wrap gap-2">
                    {ways.map((way) => (
                        <Button
                            key={way.to}
                            variant="outline"
                            size="md"
                            onClick={() => navigate(way.to)}
                        >
                            {way.label}
                        </Button>
                    ))}
                </div>
            </Card>
        );
    }

    return (
        <p
            role="alert"
            className={cn('m-0 text-[13px] leading-[1.5] text-[var(--danger)]', className)}
        >
            {problemText(problem)}
            {problem.kind === 'invoiced' ? (
                <>
                    {' '}
                    <Link
                        to={paths.billing}
                        className="text-[var(--accent)] underline underline-offset-2"
                    >
                        {inspectionsCopy.toBilling}
                    </Link>
                </>
            ) : null}
        </p>
    );
}
